import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import ProjectApi from "../../../../../api/projectApi";
import { ReactComponent as StatisticIcon } from "../../../../../assets/Certificates/merge.svg";
import { ReactComponent as BackIcon } from "../../../../../assets/Specialists/chevronLeft.svg";
import Input from "../../../../../components/Input/Input";
import ModalWindow from "../../../../../components/ModalWindow/ModalWindow";
import MuiSwitch from "../../../../../components/MuiSwitch/MuiSwitch";
import StandartButton from "../../../../../components/StandartButton/StandartButton";
import {
  Company,
  Project,
  TypeProjectParticipant,
} from "../../../../../types/Company";
import {
  ProjectsAvailableSessionTypes,
  ProjectTeamInvitation,
} from "../../../../../types/Projects";
import { getTranslation } from "../../../../../utils/getTranslation";
import Notify, { SuccesNotify } from "../../../../../utils/toaster";
import ClientCard from "./ClientCard/ClientCard";
import InviteBarterUsers from "./InviteBarterUsers/InviteBarterUsers";
import InviteStatistic from "./InviteStatistic/InviteStatistic";
import s from "./ProgramInfo.module.css";
import {
  getRowBgColor,
  getTeamMemberName,
  isSpecialistRole,
  ProjectTeamMember,
  splitUsersBySpecialty,
  TableTab,
} from "./programInfoUtils";
import SpecialistCardBlock from "./SpecialistCardBlock/SpecialistCardBlock";
import TeamInvitationsModal from "./TeamInvitationsModal";
import TeamInviteModal from "./TeamInviteModal";
import TeamMembersTable from "./TeamMembersTable";

const landingUrl = process.env.REACT_APP_LANDING_PAGE_URL;

type ProgramInfoType = {
  companyData: Company;
  selectedProgram: Project;
  setSelectedProgram: Dispatch<SetStateAction<Project | null>>;
  onBackToPrograms: () => void;
};

type UsersToInvite = {
  specialization: string[];
  email: string;
};
type ModalTarget = "experts" | "team";

const ProgramInfo = ({
  selectedProgram,
  setSelectedProgram,
  companyData,
  onBackToPrograms,
}: ProgramInfoType) => {
  const token = localStorage.getItem("token");
  const [projectSpecialists, setProjectSpecialists] = useState<
    TypeProjectParticipant[]
  >([]);
  const [projectClients, setProjectClients] = useState<
    TypeProjectParticipant[]
  >([]);
  const [activeTableTab, setActiveTableTab] = useState<TableTab>("specialists");
  const [inviteModalTarget, setInviteModalTarget] =
    useState<ModalTarget>("experts");
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [invitesModalTarget, setInvitesModalTarget] =
    useState<ModalTarget>("experts");
  const [isInvitesModalOpen, setIsInvitesModalOpen] = useState(false);
  const [specToInvite, setSpecToInvite] = useState<UsersToInvite[]>([]);
  const [copied, setCopied] = useState(false);
  const [search, setSearch] = useState({
    clients: "",
    specialists: "",
    team: "",
  });
  const [teamInvitations, setTeamInvitations] = useState<
    ProjectTeamInvitation[]
  >([]);
  const [teamActionLoader, setTeamActionLoader] = useState(false);
  const [teamInvitesLoader, setTeamInvitesLoader] = useState(false);

  const isInvitationByLinkEnabled = Boolean(
    selectedProgram?.invitationByLinkCode,
  );

  const inviteLink = useMemo(() => {
    const projectShortId = selectedProgram?.shortId ?? "";
    return `${landingUrl ?? ""}lang/project/${projectShortId}`;
  }, [selectedProgram?.shortId]);

  const discount = useMemo(() => {
    if (!selectedProgram.availableSessionTypes?.[0]) return 0;

    const packageType = selectedProgram.availableSessionTypes[0].type;
    const pricing =
      packageType === ProjectsAvailableSessionTypes.PACKAGE
        ? selectedProgram.packagePricing
        : selectedProgram.pricing;
    const discounts =
      pricing
        ?.map((item) => item.discount)
        .filter((item): item is number => item !== undefined) ?? [];

    return discounts.length ? Math.max(...discounts) : 0;
  }, [
    selectedProgram.availableSessionTypes,
    selectedProgram.packagePricing,
    selectedProgram.pricing,
  ]);

  const isClientsTab = activeTableTab === "clients";
  const isTeamTab = activeTableTab === "team";
  const tableTitle = isTeamTab
    ? "Project team"
    : isClientsTab
      ? "Clients"
      : "Specialists";
  const tableLabels = isTeamTab
    ? ["Name", "Email", "Short ID", "Roles", "Action"]
    : isClientsTab
      ? ["Name", "Email", "Age", "Location"]
      : ["Name", "Email", "Specialization", "Price"];

  const normalizedClientsSearch = search.clients.trim().toLowerCase();
  const normalizedSpecialistsSearch = search.specialists.trim().toLowerCase();
  const normalizedTeamSearch = search.team.trim().toLowerCase();

  const filteredClients = useMemo(() => {
    if (!normalizedClientsSearch) return projectClients;

    return projectClients.filter((item) => {
      const fullName = `${getTranslation(item?.name, "en")} ${getTranslation(
        item?.surname,
        "en",
      )}`
        .trim()
        .toLowerCase();
      const email = (item?.email ?? "").toLowerCase();

      return (
        fullName.includes(normalizedClientsSearch) ||
        email.includes(normalizedClientsSearch)
      );
    });
  }, [normalizedClientsSearch, projectClients]);

  const filteredSpecialists = useMemo(() => {
    if (!normalizedSpecialistsSearch) return projectSpecialists;

    return projectSpecialists.filter((item) => {
      const fullName = `${getTranslation(item.name, "en")} ${getTranslation(
        item.surname,
        "en",
      )}`
        .trim()
        .toLowerCase();
      const email = (item.email ?? "").toLowerCase();

      return (
        fullName.includes(normalizedSpecialistsSearch) ||
        email.includes(normalizedSpecialistsSearch)
      );
    });
  }, [normalizedSpecialistsSearch, projectSpecialists]);

  const projectTeam = useMemo(
    () => selectedProgram.team ?? [],
    [selectedProgram.team],
  );

  const filteredTeamMembers = useMemo(() => {
    if (!normalizedTeamSearch) return projectTeam;

    return projectTeam.filter((member) => {
      const name = getTeamMemberName(member).toLowerCase();
      const email = (member.user?.email ?? "").toLowerCase();
      const shortId = (member.user?.shortId ?? "").toLowerCase();

      return (
        name.includes(normalizedTeamSearch) ||
        email.includes(normalizedTeamSearch) ||
        shortId.includes(normalizedTeamSearch)
      );
    });
  }, [normalizedTeamSearch, projectTeam]);

  const handleCopy = async () => {
    if (!inviteLink) return;

    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Ошибка при копировании", err);
    }
  };

  const getProjectParticipants = async () => {
    if (!token) return;
    const response = await ProjectApi.getProjectParticipants(
      token,
      selectedProgram._id,
    );

    if (response.status && response.participants) {
      const participants = response.participants as TypeProjectParticipant[];
      const specialists = participants.filter((el) =>
        isSpecialistRole(el.roles),
      );

      setProjectSpecialists(splitUsersBySpecialty(specialists));
      const clients = participants.filter((el) =>
        el.roles.includes("employee"),
      );

      setProjectClients(clients);
    }
  };

  const refreshSelectedProject = async () => {
    if (!token) return;

    const project = await ProjectApi.getProjectById(token, selectedProgram._id);
    if (project) {
      setSelectedProgram(project);
    }
  };

  useEffect(() => {
    getProjectParticipants();
  }, [token, selectedProgram._id]);

  const switchInvitationWithLink = async () => {
    if (!token || !selectedProgram) return;
    const payload = {
      projectId: selectedProgram._id,
    };
    const response = await ProjectApi.switchInvitationWithLink(token, payload);
    if (response.status) {
      setSelectedProgram((prev) =>
        prev
          ? {
              ...prev,
              invitationByLinkCode: response.code,
            }
          : prev,
      );
    }
  };

  const handleRemoveTeamMember = async (member: ProjectTeamMember) => {
    if (!token) return;

    setTeamActionLoader(true);
    try {
      const response = await ProjectApi.removeTeamMember(token, {
        projectId: selectedProgram._id,
        targetUserId: member.user._id,
      });

      if (response.status) {
        SuccesNotify("Team member removed");
        setSelectedProgram((prev) =>
          prev
            ? {
                ...prev,
                team: prev.team?.filter((item) => item._id !== member._id),
              }
            : prev,
        );
      } else {
        Notify(response.message ?? "Failed to remove team member");
      }
    } catch (error) {
      console.error("Error removing project team member:", error);
      Notify("Failed to remove team member");
    } finally {
      setTeamActionLoader(false);
    }
  };

  const openInviteModal = () => {
    setInviteModalTarget(isTeamTab ? "team" : "experts");
    setIsInviteModalOpen(true);
  };

  const openInvitesModal = async () => {
    if (!isTeamTab) {
      setInvitesModalTarget("experts");
      setIsInvitesModalOpen(true);
      return;
    }

    if (!token) return;

    setInvitesModalTarget("team");
    setIsInvitesModalOpen(true);
    setTeamInvitesLoader(true);
    try {
      const response = await ProjectApi.getTeamInvitationHistory(
        token,
        selectedProgram._id,
      );

      if (response.status && response.invitations) {
        setTeamInvitations(response.invitations);
      } else {
        setTeamInvitations([]);
        if (response.message) Notify(response.message);
      }
    } catch (error) {
      console.error("Error loading project team invitations:", error);
      Notify("Failed to load team invitations");
      setTeamInvitations([]);
    } finally {
      setTeamInvitesLoader(false);
    }
  };

  return (
    <div className={s.container}>
      <div className={s.headBlock}>
        <div className={s.backBtn} role="button" onClick={onBackToPrograms}>
          <BackIcon className={s.backIcon} />
          Back to all programs
        </div>
      </div>
      <div className={s.generalInfoBlock}>
        <h2>{getTranslation(selectedProgram.title, "en")}</h2>
        <div className={s.descriptionBlock}>
          <span>Description</span>
          <div
            className={s.description}
            dangerouslySetInnerHTML={{
              __html: getTranslation(selectedProgram.description, "en"),
            }}
          ></div>
        </div>
        <div className={s.priceBlock}>
          <span>Discount: </span>
          <span className={discount === 0 ? s.discoutnZero : s.discountValue}>
            {discount === 0 ? "0" : `-${discount}%`}
          </span>
        </div>
      </div>
      {!selectedProgram.isPublicAccess && (
        <div className={s.linkSection}>
          <h3>Project Invitation Link</h3>
          <MuiSwitch
            checked={isInvitationByLinkEnabled}
            setChecked={switchInvitationWithLink}
            lable={"Enable invitations via link"}
          />
          <div
            className={s.linkWrapper}
            style={{
              opacity: isInvitationByLinkEnabled ? 1 : 0.4,
            }}
          >
            <span className={s.linkText}>{inviteLink}</span>
            <button
              type="button"
              className={s.copyButton}
              onClick={isInvitationByLinkEnabled ? handleCopy : undefined}
              disabled={!isInvitationByLinkEnabled}
              aria-label="Copy invitation link"
            >
              <ContentCopyIcon width={20} height={20} />
            </button>
            {copied && <span className={s.copiedText}>Copied!</span>}
          </div>
        </div>
      )}
      <div className={s.buttonsSection}>
        <div className={s.primaryActions}>
          <StandartButton
            buttonTitle={isTeamTab ? "Invite team members" : "Invite experts"}
            action={openInviteModal}
            height="40px"
          />
        </div>
        <div className={s.secondaryActions}>
          <button
            type="button"
            className={s.showStatsBtn}
            onClick={openInvitesModal}
          >
            <StatisticIcon className={s.statsIcon} />
            {isTeamTab ? "Show team invites" : "Show expert invites"}
          </button>
        </div>
      </div>
      <div className={s.tableBlock}>
        <div className={s.headTableBlock}>
          <div className={s.tabsBlock}>
            <button
              type="button"
              className={`${s.tableTab} ${s.firstTableTab} ${
                activeTableTab === "specialists" ? s.activeTab : s.notActivetab
              }`}
              onClick={() => setActiveTableTab("specialists")}
            >
              Specialists
            </button>
            <button
              type="button"
              className={`${s.tableTab} ${
                activeTableTab === "clients" ? s.activeTab : s.notActivetab
              }`}
              onClick={() => setActiveTableTab("clients")}
            >
              Clients
            </button>
            <button
              type="button"
              className={`${s.tableTab} ${s.lastTableTab} ${
                activeTableTab === "team" ? s.activeTab : s.notActivetab
              }`}
              onClick={() => setActiveTableTab("team")}
            >
              Team
            </button>
          </div>
          <div className={s.tableTitleBlock}>{tableTitle}</div>

          <Input
            inputValue={search[activeTableTab]}
            onChangeInput={(value) =>
              setSearch((prev) => ({ ...prev, [activeTableTab]: value }))
            }
            placeholder={`Search ${tableTitle.toLowerCase()}...`}
            bgColor="black"
            width="20%"
          />
        </div>
        <div
          className={`${s.tableLabels} ${isTeamTab ? s.teamTableLabels : ""}`}
        >
          {tableLabels.map((label) => (
            <span key={label}>{label}</span>
          ))}
        </div>
        {isTeamTab ? (
          <TeamMembersTable
            members={filteredTeamMembers}
            isLoading={teamActionLoader}
            search={search.team}
            onRemove={handleRemoveTeamMember}
          />
        ) : isClientsTab ? (
          <div className={s.clientsListBlock}>
            {filteredClients.length ? (
              filteredClients.map((item, index) => (
                <ClientCard
                  key={item._id}
                  bgColor={getRowBgColor(index)}
                  userId={item._id}
                />
              ))
            ) : (
              <div className={s.noDataBlock}>
                <h3>
                  {normalizedClientsSearch
                    ? "No clients found"
                    : "No clients in this project"}
                </h3>
              </div>
            )}
          </div>
        ) : (
          <div className={s.specialistsListBlock}>
            {filteredSpecialists.length ? (
              filteredSpecialists.map((item, index) => (
                <SpecialistCardBlock
                  key={item._id}
                  bgColor={getRowBgColor(index)}
                  user={item}
                  discount={discount}
                  projectId={selectedProgram._id}
                  setProjectSpecialists={setProjectSpecialists}
                  specialistsToInvite={specToInvite}
                  setSpecialistsToInvite={setSpecToInvite}
                  specializationsData={
                    item.specialistIds &&
                    item.specialistIds.map((item) => ({
                      specialistId: item._id,
                      specialization:
                        item.mainSpecializations[0].specialization,
                      specializationId: item.mainSpecializations[0]._id,
                    }))
                  }
                  projectData={selectedProgram}
                />
              ))
            ) : (
              <div className={s.noDataBlock}>
                <h3>
                  {normalizedSpecialistsSearch
                    ? "No specialists found"
                    : "No specialist in this project"}
                </h3>
              </div>
            )}
          </div>
        )}
      </div>
      <ModalWindow
        isOpen={isInviteModalOpen && inviteModalTarget === "experts"}
        setIsOpen={setIsInviteModalOpen}
        width="fit-content"
        padding="20px"
      >
        <InviteBarterUsers
          setModalVisible={setIsInviteModalOpen}
          projectData={selectedProgram}
          isBarter={selectedProgram.isBarter}
        />
      </ModalWindow>
      <TeamInviteModal
        isOpen={isInviteModalOpen && inviteModalTarget === "team"}
        setIsOpen={setIsInviteModalOpen}
        projectId={selectedProgram._id}
        existingTeamUserIds={projectTeam.map((member) => member.user._id)}
        onInvitesSent={refreshSelectedProject}
      />
      {selectedProgram?.entryCodes && (
        <ModalWindow
          isOpen={isInvitesModalOpen && invitesModalTarget === "experts"}
          setIsOpen={setIsInvitesModalOpen}
          width="fit-content"
        >
          <InviteStatistic
            entryCodes={selectedProgram?.entryCodes}
            setModalIsOpen={setIsInvitesModalOpen}
          />
        </ModalWindow>
      )}
      <TeamInvitationsModal
        isOpen={isInvitesModalOpen && invitesModalTarget === "team"}
        setIsOpen={setIsInvitesModalOpen}
        invitations={teamInvitations}
        isLoading={teamInvitesLoader}
      />
    </div>
  );
};

export default ProgramInfo;
